import React, { useEffect, useRef, useCallback } from 'react';
import { defineCustomElements } from 'nerdagent-chat-widget/loader';

let customElementsDefined = false;

export type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface ChatWidgetProps {
  apiKey?: string;
  agentId?: string;
  token?: string;
  agentName?: string;
  agentRole?: string;
  primaryColor?: string;
  accentColor?: string;
  welcomeMessage?: string;
  placeholderText?: string;
  position?: WidgetPosition;
  width?: string;
  height?: string;
  showMinimizeButton?: boolean;
  showTimestamps?: boolean;
  enableFileUpload?: boolean;
  enableSpeech?: boolean;
  showPoweredBy?: boolean;
  enableDebug?: string;
  enablePulsingAnimation?: boolean;
  onMessageSent?: (event: { message: string; timestamp: Date }) => void;
  onWidgetOpened?: () => void;
  onWidgetClosed?: () => void;
}

export const NerdChatWidget: React.FC<ChatWidgetProps> = ({
  apiKey,
  agentId,
  token,
  agentName = 'Support Agent',
  agentRole = 'Customer Support',
  primaryColor = '#2d3e50',
  accentColor = '#4e8cff',
  welcomeMessage = 'Hi! How can I help?',
  placeholderText = 'Type your message...',
  position = 'bottom-right',
  width = '350',
  height = '500',
  showMinimizeButton = true,
  showTimestamps = true,
  enableFileUpload = false,
  enableSpeech = false,
  showPoweredBy = true,
  enableDebug = '',
  enablePulsingAnimation = true,
  onMessageSent,
  onWidgetOpened,
  onWidgetClosed,
}) => {
  const widgetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!customElementsDefined) {
      defineCustomElements();
      customElementsDefined = true;
    }
  }, []);

  const handleMessageSent = useCallback((event: CustomEvent) => {
    onMessageSent?.(event.detail);
  }, [onMessageSent]);

  const handleWidgetOpened = useCallback((event: CustomEvent) => {
    onWidgetOpened?.();
  }, [onWidgetOpened]);

  const handleWidgetClosed = useCallback((event: CustomEvent) => {
    onWidgetClosed?.();
  }, [onWidgetClosed]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;

    widget.addEventListener('messageSent', handleMessageSent as EventListener);
    widget.addEventListener('widgetOpened', handleWidgetOpened as EventListener);
    widget.addEventListener('widgetClosed', handleWidgetClosed as EventListener);

    return () => {
      widget.removeEventListener('messageSent', handleMessageSent as EventListener);
      widget.removeEventListener('widgetOpened', handleWidgetOpened as EventListener);
      widget.removeEventListener('widgetClosed', handleWidgetClosed as EventListener);
    };
  }, [handleMessageSent, handleWidgetOpened, handleWidgetClosed]);

  const props: any = {
    ref: widgetRef,
    'agent-name': agentName,
    'agent-role': agentRole,
    'primary-color': primaryColor,
    'accent-color': accentColor,
    'welcome-message': welcomeMessage,
    'placeholder-text': placeholderText,
    position: position,
    width: width,
    height: height,
    'show-minimize-button': String(showMinimizeButton),
    'show-timestamps': String(showTimestamps),
    'enable-file-upload': String(enableFileUpload),
    'enable-speech': String(enableSpeech),
    'show-powered-by': String(showPoweredBy),
    'enable-debug': enableDebug,
    'enable-pulsing-animation': String(enablePulsingAnimation),
  };

  if (apiKey) props.apikey = apiKey;
  if (agentId) props['agent-id'] = agentId;
  if (token) props.token = token;

  return React.createElement('nerd-chat-widget', props);
};

export default NerdChatWidget;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'nerd-chat-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        apikey?: string;
        'agent-id'?: string;
        token?: string;
        'agent-name'?: string;
        'agent-role'?: string;
        'primary-color'?: string;
        'accent-color'?: string;
        'welcome-message'?: string;
        'placeholder-text'?: string;
        position?: WidgetPosition;
        width?: string;
        height?: string;
        'show-minimize-button'?: string;
        'show-timestamps'?: string;
        'enable-file-upload'?: string;
        'enable-speech'?: string;
        'show-powered-by'?: string;
      };
    }
  }
}
